import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { proxyCoverUrl } from "../utils/coverProxy";

export function SearchSpotlight({ variant = "desktop" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/books`, { params: { search: q, limit: 6 } });
      setResults(data.books || []);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    setSelectedIdx(-1);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const goToBook = (bookId) => {
    setOpen(false);
    setQuery("");
    setResults([]);
    navigate(`/book/${bookId}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && selectedIdx >= 0 && results[selectedIdx]) {
      goToBook(results[selectedIdx].book_id);
    }
  };

  const CoverFallback = ({ title }) => (
    <div className="w-full h-full flex items-center justify-center text-[9px] font-medium text-center p-1 leading-tight"
      style={{ background: 'linear-gradient(135deg, var(--muted) 0%, var(--hero) 100%)', color: 'var(--on-hero)' }}>
      {title}
    </div>
  );

  // Trigger button (placeholder that looks like a search bar)
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`${variant === "desktop" ? "hidden sm:flex" : "flex"} items-center gap-2 h-9 px-4 rounded-full text-xs cursor-text`}
        style={{ background: 'rgba(255,246,230,0.1)', border: '1px solid rgba(248,245,240,0.15)' }}
        data-testid="search-spotlight-trigger"
      >
        <Search className="w-3.5 h-3.5" style={{ color: 'var(--on-hero-subtle)' }} strokeWidth={1.5} />
        <span style={{ color: 'var(--on-hero-subtle)' }}>Search books...</span>
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative z-50" data-testid="search-spotlight-container">
      {/* Input */}
      <div className="flex items-center gap-2 h-9 px-3 rounded-full text-xs"
        style={{ background: 'rgba(248,245,240,0.15)', border: '1px solid rgba(212,180,131,0.4)' }}>
        <Search className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--highlight)' }} strokeWidth={1.5} />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search by title or author..."
          className="bg-transparent outline-none w-[180px] sm:w-[220px] text-xs"
          style={{ color: 'var(--on-hero)' }}
          data-testid="search-spotlight-input"
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults([]); }} data-testid="search-spotlight-clear">
            <X className="w-3.5 h-3.5" style={{ color: 'var(--on-hero-subtle)' }} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {(query.length >= 2) && (
        <div className="absolute top-11 right-0 w-[320px] sm:w-[380px] rounded-xl overflow-hidden shadow-2xl"
          style={{ background: 'var(--hero)', border: '1px solid var(--highlight-border)' }}
          data-testid="search-spotlight-dropdown">
          {loading && (
            <div className="px-4 py-3 text-xs" style={{ color: 'var(--on-hero-subtle)' }}>Searching...</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-4 py-4 text-xs text-center" style={{ color: 'var(--on-hero-subtle)' }}>
              No books found for "{query}"
            </div>
          )}
          {!loading && results.length > 0 && (
            <div className="py-1.5">
              {results.map((book, idx) => (
                <button
                  key={book.book_id}
                  onClick={() => goToBook(book.book_id)}
                  className="w-full flex items-center gap-3 px-3 py-2 transition-colors text-left"
                  style={{
                    background: selectedIdx === idx ? 'rgba(212,180,131,0.12)' : 'transparent',
                  }}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  data-testid={`search-result-${book.book_id}`}
                >
                  {/* Cover thumbnail */}
                  <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0" style={{ background: 'var(--hero-dark)' }}>
                    {book.cover_image ? (
                      <img
                        src={proxyCoverUrl(book.cover_image)}
                        alt={book.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                      />
                    ) : null}
                    <div className="hidden w-full h-full items-center justify-center text-[8px] font-medium text-center p-0.5 leading-tight"
                      style={{ background: 'linear-gradient(135deg, var(--muted), var(--hero))', color: 'var(--on-hero)' }}>
                      {book.title}
                    </div>
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate" style={{ color: 'var(--on-hero)' }}>{book.title}</div>
                    <div className="text-[11px] truncate mt-0.5" style={{ color: 'var(--on-hero-subtle)' }}>{book.author}</div>
                    {book.category && (
                      <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(212,180,131,0.15)', color: 'var(--highlight)' }}>
                        {book.category}
                      </span>
                    )}
                  </div>
                  {/* Rating */}
                  {book.rating && (
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <span className="text-[11px]" style={{ color: 'var(--highlight)' }}>★</span>
                      <span className="text-[11px]" style={{ color: 'var(--on-hero-muted)' }}>{book.rating}</span>
                    </div>
                  )}
                </button>
              ))}
              {results.length >= 6 && (
                <button
                  onClick={() => { setOpen(false); setQuery(""); navigate("/browse"); }}
                  className="w-full text-center py-2 text-[11px] font-medium transition-colors"
                  style={{ color: 'var(--highlight)', borderTop: '1px solid rgba(212,180,131,0.1)' }}
                  data-testid="search-view-all"
                >
                  View all results →
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
